import { createApi } from '@reduxjs/toolkit/query/react';
import { publicBaseQuery } from '@core/api';

/**
 * Public Step API using RTK Query
 *
 * PUBLIC API - No authentication required
 * Endpoints for employees to complete onboarding steps via token-based links.
 */
export const publicStepApi = createApi({
  reducerPath: 'publicStepApi',
  baseQuery: publicBaseQuery,
  tagTypes: ['PublicStep'],
  endpoints: (builder) => ({
    /**
     * Get public step details by token
     * @param {string} token - Step access token
     */
    getPublicStep: builder.query({
      query: (token) => `/api/v1/public/onboarding/${token}/detail/`,
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, token) => [{ type: 'PublicStep', id: token }],
    }),

    /**
     * Get a presigned URL for uploading a file to S3
     * @param {Object} params
     * @param {string} params.token - Step access token
     * @param {string} params.file_name - File name
     * @param {string} params.content_type - MIME type
     * @param {string} [params.side] - 'front' or 'back' (for identity documents)
     */
    getUploadUrl: builder.mutation({
      query: ({ token, ...body }) => ({
        url: `/api/v1/public/onboarding/${token}/upload-url/`,
        method: 'POST',
        body,
      }),
    }),

    /**
     * Check one uploaded document photo (wrong document/side, cut off, glare, blurry)
     * @param {Object} params
     * @param {string} params.token - Step access token
     * @param {string} params.file_key - S3 key returned by getUploadUrl
     * @param {string} [params.document_key] - Sub-document (initial_documentation)
     * @param {string} params.side - Upload zone side (front, back, nie_front, nie_back, passport_front)
     * @returns {{ result: 'ok'|'warning'|'rejected', issue: string|null, params: Object, checked: boolean }}
     */
    checkPhoto: builder.mutation({
      query: ({ token, ...body }) => ({
        url: `/api/v1/public/onboarding/${token}/check-photo/`,
        method: 'POST',
        body,
      }),
    }),

    /**
     * Complete a public step
     * @param {Object} params
     * @param {string} params.token - Step access token
     * @param {Object} params.response_data - Step completion data
     */
    completeStep: builder.mutation({
      query: ({ token, ...body }) => ({
        url: `/api/v1/public/onboarding/${token}/complete/`,
        method: 'POST',
        body,
      }),
    }),

    /**
     * Get the filled SIGN_DOCUMENT PDF as an object URL for preview
     * @param {string} token - Step access token
     */
    getSignDocumentPreview: builder.query({
      query: (token) => ({
        url: `/api/v1/public/onboarding/${token}/document-preview/`,
        responseHandler: (response) => response.blob(),
      }),
      // Store an object URL (string) instead of the Blob itself
      transformResponse: (blob) => URL.createObjectURL(blob),
      keepUnusedDataFor: 0,
    }),

    /**
     * Save progress on a multi-document step (initial_documentation)
     * @param {Object} params
     * @param {string} params.token - Step access token
     * @param {string} params.document_key - Key of the sub-document completed
     * @param {Object} params.document_data - Data for the completed sub-document
     */
    saveProgress: builder.mutation({
      query: ({ token, ...body }) => ({
        url: `/api/v1/public/onboarding/${token}/save-progress/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { token }) => [{ type: 'PublicStep', id: token }],
    }),
  }),
});

export const {
  useCheckPhotoMutation,
  useGetPublicStepQuery,
  useGetUploadUrlMutation,
  useCompleteStepMutation,
  useGetSignDocumentPreviewQuery,
  useSaveProgressMutation,
} = publicStepApi;
